import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  EnvelopeSimple,
  GoogleLogo,
  Lock,
  ShieldCheck,
  SignOut,
  User,
  CalendarBlank,
} from '@phosphor-icons/react';
import {
  REAUTH_MINUTES,
  SECURITY_GRACE_DAYS,
  STAFF_ROLES,
  type SecurityPolicyResponse,
  type SecurityPolicy,
  type StaffRole,
} from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { staffHealthQuery, useSectionPatch } from '../../../../../../lib/workspace-api';
import { useStaffReauth } from '../../../../../../components/settings/use-staff-reauth';
import { LastChanged, RowValue, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { ValueEditorDialog, type ValueEditor } from '../../../../../../components/settings/value-editor-dialog';
import { Avatar } from '../../../../../../components/ui/avatar';
import { Button } from '../../../../../../components/ui/button';
import { Checkbox } from '../../../../../../components/ui/checkbox';
import { Dialog } from '../../../../../../components/ui/dialog';
import { Toggle } from '../../../../../../components/ui/toggle';
import { useToast } from '../../../../../../components/ui/toast';
import { formatChangedDate } from '../../../../../../lib/format-edited';
import { initialsOf } from '../../../../../../lib/initials';
import { ROLE_LABEL } from '../../../../../../lib/staff-nav';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/security-policy')({
  component: SecurityPolicyPage,
});

const LOCK_PRESETS = [
  { tries: 3, minutes: 15 },
  { tries: 5, minutes: 15 },
  { tries: 5, minutes: 30 },
  { tries: 10, minutes: 30 },
];
const lockLabel = (tries: number, minutes: number) => `${tries} tries · ${minutes} min`;

type Editing =
  { field: 'graceDays' | 'sessionDays' | 'idleHours' | 'reauthMinutes' | 'emailDomains' | 'lock' } | undefined;

function rolesSummary(roles: StaffRole[]) {
  if (roles.length === STAFF_ROLES.length) return 'All staff roles';
  return roles.map((r) => ROLE_LABEL[r]).join(', ');
}

function SecurityPolicyPage() {
  const { data: policy, patch } = useSectionPatch('securityPolicy');
  if (!policy) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <SecurityPolicyForm policy={policy} save={patch} />;
}

function SecurityPolicyForm({
  policy,
  save,
}: {
  policy: SecurityPolicyResponse;
  save: (changes: Partial<SecurityPolicy>) => void;
}) {
  const toast = useToast();
  const { guard, dialog: reauthDialog } = useStaffReauth();

  const [editing, setEditing] = useState<Editing>();
  const [rolesOpen, setRolesOpen] = useState(false);
  const [impactOpen, setImpactOpen] = useState(false);

  // The people who would be affected by requiring two-step.
  const roster = useQuery({ ...staffHealthQuery, retry: false });
  const withoutTwoFactor = (roster.data ?? []).filter(
    (s) => !s.twoFactorEnabled && policy.appliesTo.includes(s.staffRole),
  );

  const editorFor = (
    field: NonNullable<Editing>['field'],
  ): { title: string; label: string; editor: ValueEditor; onSave: (v: string | number) => void } => {
    switch (field) {
      case 'graceDays':
        return {
          title: 'Time to set it up',
          label: 'After this, we ask for it at their next sign-in',
          editor: {
            kind: 'select',
            value: String(policy.graceDays),
            options: SECURITY_GRACE_DAYS.map((d) => ({ value: String(d), label: `${d} ${d === 1 ? 'day' : 'days'}` })),
          },
          onSave: (v) => save({ graceDays: Number(v) }),
        };
      case 'sessionDays':
        return {
          title: 'Stay signed in for',
          label: 'Days',
          editor: {
            kind: 'number',
            value: policy.sessionDays,
            min: 1,
            max: 90,
            unit: 'People sign in again after this many days.',
          },
          onSave: (v) => save({ sessionDays: Number(v) }),
        };
      case 'idleHours':
        return {
          title: 'Sign out after no activity',
          label: 'Hours',
          editor: {
            kind: 'number',
            value: policy.idleHours,
            min: 1,
            max: 72,
            unit: 'Hours without any activity before signing out.',
          },
          onSave: (v) => save({ idleHours: Number(v) }),
        };
      case 'reauthMinutes':
        return {
          title: 'Ask for password again',
          label: 'Before sensitive actions',
          editor: {
            kind: 'select',
            value: String(policy.reauthMinutes),
            options: REAUTH_MINUTES.map((m) => ({ value: String(m), label: `Every ${m} minutes` })),
          },
          onSave: (v) => save({ reauthMinutes: Number(v) }),
        };
      case 'emailDomains':
        return {
          title: 'Staff email domains',
          label: 'Domains (separate with commas)',
          editor: { kind: 'text', value: policy.emailDomains.join(', '), placeholder: 'mybusinessmate.ai' },
          onSave: (v) => {
            const domains = String(v)
              .split(',')
              .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
              .filter(Boolean);
            if (domains.length > 0) save({ emailDomains: domains });
          },
        };
      case 'lock': {
        const current = lockLabel(policy.lockAfterTries, policy.lockMinutes);
        const options = LOCK_PRESETS.map((p) => lockLabel(p.tries, p.minutes));
        return {
          title: 'Lock after failed sign-ins',
          label: 'Unlocks on its own, or an admin can unlock from Staff',
          editor: {
            kind: 'select',
            value: current,
            options: (options.includes(current) ? options : [current, ...options]).map((o) => ({ value: o, label: o })),
          },
          onSave: (v) => {
            const [tries, minutes] = String(v).match(/\d+/g)?.map(Number) ?? [];
            if (tries && minutes) save({ lockAfterTries: tries, lockMinutes: minutes });
          },
        };
      }
    }
  };
  const active = editing ? editorFor(editing.field) : undefined;

  return (
    <>
      <SettingCard
        title="Two-step verification"
        subtitle="Staff can see client logins and files, so two-step is strongly recommended."
      >
        <SettingRow
          icon={ShieldCheck}
          title="Require two-step for staff"
          subtitle={
            policy.requireTwoFactor
              ? `On · ${rolesSummary(policy.appliesTo)}`
              : 'Off · staff can still turn it on themselves'
          }
          trailing={
            <Toggle
              aria-label="Require two-step for staff"
              checked={policy.requireTwoFactor}
              onCheckedChange={(on) =>
                on
                  ? guard('require two-step for all staff', () => setImpactOpen(true))
                  : guard('stop requiring two-step', () => {
                      save({ requireTwoFactor: false });
                      toast({ title: 'Two-step is no longer required' });
                    })
              }
            />
          }
        />
        <SettingRow
          icon={User}
          title="Applies to"
          subtitle="Pick roles when you turn it on"
          trailing={<RowValue>{rolesSummary(policy.appliesTo)}</RowValue>}
          onClick={() => setRolesOpen(true)}
        />
        <SettingRow
          icon={CalendarBlank}
          title="Time to set it up"
          subtitle="After this, we ask for it at their next sign-in"
          trailing={<RowValue>{policy.graceDays} days</RowValue>}
          onClick={() => setEditing({ field: 'graceDays' })}
        />
      </SettingCard>

      <SettingCard title="Sessions" subtitle="How long people stay signed in.">
        <SettingRow
          icon={Clock}
          title="Stay signed in for"
          trailing={<RowValue>{policy.sessionDays} days</RowValue>}
          onClick={() => setEditing({ field: 'sessionDays' })}
        />
        <SettingRow
          icon={SignOut}
          title="Sign out after no activity"
          trailing={<RowValue>{policy.idleHours} hours</RowValue>}
          onClick={() => setEditing({ field: 'idleHours' })}
        />
        <SettingRow
          icon={Lock}
          title="Ask for password again before sensitive actions"
          subtitle="Viewing client logins, backup codes, data exports"
          trailing={<RowValue>Every {policy.reauthMinutes} minutes</RowValue>}
          onClick={() => setEditing({ field: 'reauthMinutes' })}
        />
      </SettingCard>

      <SettingCard title="Sign-in rules" subtitle="Who can sign in to the staff app, and how.">
        <SettingRow
          icon={EnvelopeSimple}
          title="Staff email domains"
          subtitle="Invites to other domains are blocked"
          trailing={<RowValue>{policy.emailDomains.join(', ')}</RowValue>}
          onClick={() => setEditing({ field: 'emailDomains' })}
        />
        <SettingRow
          icon={GoogleLogo}
          title="Allow Google sign-in for staff"
          subtitle="Only for accounts on the domains above"
          trailing={
            <Toggle
              aria-label="Allow Google sign-in for staff"
              checked={policy.allowGoogleSignIn}
              onCheckedChange={(on) => save({ allowGoogleSignIn: on })}
            />
          }
        />
        <SettingRow
          icon={Lock}
          title="Lock after failed sign-ins"
          subtitle="Unlocks on its own, or an admin can unlock from Staff"
          trailing={<RowValue>{lockLabel(policy.lockAfterTries, policy.lockMinutes)}</RowValue>}
          onClick={() => setEditing({ field: 'lock' })}
        />
      </SettingCard>

      <LastChanged by={policy.changed?.by} date={policy.changed ? formatChangedDate(policy.changed.at) : undefined} />

      {reauthDialog}

      <Dialog
        open={impactOpen}
        onClose={() => setImpactOpen(false)}
        icon={ShieldCheck}
        title="Require two-step for all staff?"
        description={
          withoutTwoFactor.length > 0
            ? `${withoutTwoFactor.length} ${withoutTwoFactor.length === 1 ? 'person hasn’t' : 'people haven’t'} set it up yet. We’ll email them now. They get ${policy.graceDays} days, then they must set it up at their next sign-in.`
            : `Anyone who hasn’t set it up yet gets an email now. They get ${policy.graceDays} days, then they must set it up at their next sign-in.`
        }
      >
        {withoutTwoFactor.length > 0 ? (
          <ul className="flex flex-col gap-(--mbm-space-3) rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-4)">
            {withoutTwoFactor.map((s) => (
              <li key={s.id} className="flex items-center gap-(--mbm-space-3)">
                <Avatar initials={initialsOf(s.name, s.email)} size="sm" className="bg-brand-primary" />
                <span className="text-label-medium text-text-primary">{s.name}</span>
                <span className="text-body-small text-text-tertiary">{ROLE_LABEL[s.staffRole]}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="grid grid-cols-2 gap-(--mbm-space-3)">
          <Button variant="secondary" size="compact" onClick={() => setImpactOpen(false)}>
            Cancel
          </Button>
          <Button
            size="compact"
            onClick={() => {
              save({ requireTwoFactor: true });
              setImpactOpen(false);
              toast({ title: 'Two-step is now required for staff' });
            }}
          >
            Require two-step
          </Button>
        </div>
        <p className="text-body-xsmall text-text-tertiary">Nobody gets signed out now. You can turn this off later.</p>
      </Dialog>

      <Dialog
        open={rolesOpen}
        onClose={() => setRolesOpen(false)}
        title="Applies to"
        description="Pick the roles that must use two-step."
      >
        {rolesOpen ? (
          <RolePicker
            value={policy.appliesTo}
            onCancel={() => setRolesOpen(false)}
            onSave={(roles) => {
              save({ appliesTo: roles });
              setRolesOpen(false);
            }}
          />
        ) : null}
      </Dialog>

      {active ? (
        <ValueEditorDialog
          open
          onClose={() => setEditing(undefined)}
          title={active.title}
          label={active.label}
          editor={active.editor}
          onSave={active.onSave}
        />
      ) : null}
    </>
  );
}

function RolePicker({
  value,
  onCancel,
  onSave,
}: {
  value: StaffRole[];
  onCancel: () => void;
  onSave: (roles: StaffRole[]) => void;
}) {
  const [roles, setRoles] = useState<StaffRole[]>(value);
  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <div className="flex flex-col gap-(--mbm-space-3)">
        {STAFF_ROLES.map((role) => (
          <Checkbox
            key={role}
            label={ROLE_LABEL[role]}
            checked={roles.includes(role)}
            onChange={(e) => setRoles((r) => (e.target.checked ? [...r, role] : r.filter((x) => x !== role)))}
          />
        ))}
      </div>
      {roles.length === 0 ? <p className="text-body-small text-feedback-error">Pick at least one role</p> : null}
      <div className="grid grid-cols-2 gap-(--mbm-space-3)">
        <Button variant="secondary" size="compact" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="compact" disabled={roles.length === 0} onClick={() => onSave(roles)}>
          Save
        </Button>
      </div>
    </div>
  );
}
